import { useEffect, useMemo, useState } from 'preact/hooks'
import { addCartItem, clearBundlesChildCart, getCart, getMaxQuantityByVariant } from 'libs/cart'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import { PRODUCT_TYPES, SAMPLE_CART_PROPERTY_KEY } from 'helpers/constants'
import clsx from 'clsx'
import ButtonATC from 'snippets/button-atc/button-atc'
import ButtonBIS from 'snippets/button-atc/button-bis'
import ButtonATCCustom from 'snippets/button-atc-custom/button-atc-custom'

function PlpQuickAdd({
  product,
  currentVariant,
  hiddenPrice = false,
  textButton = 'Add',
  classButton,
  customData = {},
  hasNoNotifyMeTag = false,
  isDisabledAllButton = false,
  productBundles = [],
  useCustomButtonAtc = false,
  buttonTextClass = 'lg-max:hidden',
  isMobileLayout = false,
  setIsDisabledAllButton = () => {}
}) {
  const [isAdding, setIsAdding] = useState(false)
  const [maxQuantity, setMaxQuantity] = useState(null)
  const [cartItems, setCartItems] = useState(GM_STATE.cart.initCart.items)
  const [quantity, setQuantity] = useState(1)
  const { price, available, compare_at_price: originalPrice } = currentVariant
  const enableBackInStock = !available && !hasNoNotifyMeTag

  const isProductSample = Object.keys(customData).length > 0
  const isSampleDisabled = useMemo(() => {
    if (!isProductSample || !customData.listSample?.length) return false

    return customData.listSample.some(
      (item) => item.id === currentVariant.id && item.properties?.[SAMPLE_CART_PROPERTY_KEY]
    )
  }, [customData, currentVariant])

  const isDisabled = useMemo(
    () => quantity > maxQuantity || isSampleDisabled || customData?.disabled,
    [quantity, maxQuantity, isSampleDisabled, customData]
  )
  const isGiftCard = product?.isGiftCard
  const buttonSampleText = isSampleDisabled ? 'Added' : 'Add'
  const buttonText = isProductSample ? buttonSampleText : textButton

  const syncMaxQuantity = async (newCart) => {
    setCartItems(newCart.items)

    const maxValue = await getMaxQuantityByVariant(newCart.items, currentVariant)

    setMaxQuantity(maxValue)
  }

  const resetQuantity = (value = 1) => {
    setQuantity(value)
  }

  const addToCart = async () => {
    try {
      const itemsCart = GM_STATE.cart.initCart.items
      const isProductBundle = product.type === PRODUCT_TYPES.VALUE_BUNDLE
      const isSampleProduct = product?.metafields?.seoHidden === '1'

      setIsAdding(true)
      setIsDisabledAllButton(true)

      if (isSampleProduct) {
        const cart = await getCart()
        const { maxItemsAdded } = window.GM_STATE.cart.samples
        const countSampleItem = cart.items.filter((item) => item.properties?.[SAMPLE_CART_PROPERTY_KEY])
        const hasSampleInCart = cart.items.some(
          (item) => item.id === currentVariant.id && item.properties?.[SAMPLE_CART_PROPERTY_KEY]
        )
        const messageError = hasSampleInCart ? translate.general.sampleAddedError : translate.general.sampleMaximumError

        if (hasSampleInCart || countSampleItem.length >= maxItemsAdded) {
          globalEmitter.emit(emitterProps.notice.global, {
            type: 'warning',
            content: messageError,
            duration: 3000
          })

          return
        }
      }

      if (isProductBundle && itemsCart.length && productBundles.length) {
        await clearBundlesChildCart(itemsCart, product.id, productBundles)
      }

      const properties = isProductSample ? { ...customData.properties } : {}
      const response = await addCartItem(currentVariant.id, properties, 1)

      if (!response) throw new Error('Can not add to cart')

      const newCart = await getCart()

      globalEmitter.emit(emitterProps.cart.add, newCart)

      const max = getMaxQuantityByVariant(newCart.items, currentVariant)

      resetQuantity(max > 0 && max < quantity ? max : 1)
      await syncMaxQuantity(newCart)
    } catch (error) {
      console.error(error)
    } finally {
      setIsAdding(false)
      setIsDisabledAllButton(false)
    }
  }

  const updateMaxQuantity = (items) => {
    const maxValue = getMaxQuantityByVariant(items, currentVariant)

    setCartItems(items)
    setMaxQuantity(maxValue)
  }

  const cartUpdateListener = (items) => updateMaxQuantity(items, currentVariant)

  useEffect(() => {
    if (currentVariant) {
      resetQuantity(1)
      updateMaxQuantity(cartItems, currentVariant)
      globalEmitter.on(emitterProps.cart.update, cartUpdateListener)
    }

    return () => {
      globalEmitter.off(emitterProps.cart.update, cartUpdateListener)
    }
  }, [currentVariant])

  if (enableBackInStock) {
    return (
      <ButtonBIS
        product={product}
        currentVariant={currentVariant}
        buttonText={translate.product.notifyMe}
        classButton={clsx(
          useCustomButtonAtc
            ? 'relative w-full text-center uppercase px-2 text-xs pt-[13px] pb-2.5 min-h-[43px] border-t border-neutral-900 mt-auto'
            : 'button-outlined relative w-full px-2 uppercase',
          !isMobileLayout && useCustomButtonAtc && 'transition-opacity duration-300 xl:opacity-0 group-hover:xl:opacity-100',
          classButton
        )}
      />
    )
  }

  if (isGiftCard) {
    return (
      <a
        href={`/products/${product.handle}`}
        className={clsx(
          useCustomButtonAtc
            ? 'relative mt-auto min-h-[43px] w-full border-t border-neutral-900 text-neutral-900 px-2 pb-2.5 pt-[13px] text-center text-xs uppercase transition-opacity duration-300 xl:opacity-0 group-hover:xl:opacity-100'
            : 'button-outlined relative flex w-full items-center justify-center px-3 lg:px-4',
          classButton
        )}
      >
        {translate.product.chooseValue}
      </a>
    )
  }

  if (!available) {
    return (
      <ButtonATC
        disabled
        text={translate.collection.soldOut}
        buttonClass={clsx(
          'button-outlined flex justify-center items-center px-3 lg:px-4 pointer-events-none',
          classButton,
          useCustomButtonAtc && 'min-h-11 px-2 pb-2.5 pt-3 text-center text-xs uppercase !text-neutral-900 border-t !border-t-neutral-900 opacity-30 font-normal !bg-transparent',
          !isMobileLayout && useCustomButtonAtc && 'transition-opacity duration-300 xl:opacity-0 group-hover:xl:opacity-30'
        )}
        hiddenMarkDash
        hiddenPrice
      />
    )
  }

  if (useCustomButtonAtc) {
    return (
      <ButtonATCCustom
        disabled={maxQuantity <= 0 || isDisabled || isDisabledAllButton || isAdding}
        text={textButton}
        onHandle={addToCart}
        price={price}
        originalPrice={originalPrice}
        loading={isAdding}
        buttonClass={clsx('flex justify-between items-center', classButton)}
        buttonTextClass={buttonTextClass}
        isMobileLayout={isMobileLayout}
      />
    )
  }

  return (
    <ButtonATC
      disabled={maxQuantity <= 0 || isDisabled || isDisabledAllButton}
      text={buttonText}
      onHandle={addToCart}
      price={price}
      originalPrice={originalPrice}
      loading={isAdding}
      buttonClass={clsx(
        'button-outlined flex items-center px-3 lg:px-4',
        classButton,
        isProductSample
          ? 'justify-center disabled:!border-neutral-300'
          : 'justify-between'
      )}
      hiddenMarkDash
      hiddenPrice={isProductSample || hiddenPrice}
    />
  )
}

export default PlpQuickAdd
